import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, FileText, Loader2 } from 'lucide-react';
import { Answer, DocumentFile } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { FileUpload } from '@/components/FileUpload';
import { FileUtils } from '@/utils/fileUtils';
import { fileService } from '@/api/files/file.service';
import { api } from '@/api/axios';
import imageCompression from 'browser-image-compression';
interface DocumentInputProps {
  answer: Answer;
  onBlur: (value: { documentValue: DocumentFile[] }) => void;
  disabled?: boolean;
}

const DEFAULT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
];

function resolvePublicUrl(url: string) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;

  const baseURL = api.defaults.baseURL || '';
  const apiRoot = baseURL.replace(/\/api\/v1\/?$/, '');
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${apiRoot}${normalizedPath}`;
}

export function DocumentInput({ answer, onBlur, disabled }: DocumentInputProps) {
  const [isSaved, setIsSaved] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentDocument = answer.documentValue?.[0] || null;
  const acceptedMimeTypes =
    answer.question.validationRules?.acceptedMimeTypes?.length
      ? answer.question.validationRules.acceptedMimeTypes
      : DEFAULT_MIME_TYPES;

  const accept = useMemo(() => acceptedMimeTypes.join(','), [acceptedMimeTypes]);



const handleFileSelect = async (file: File) => {
  if (disabled) return;

  try {
    setError(null);
    setIsUploading(true);

    let compressedFile: File;

    try {

      // Solo comprimir imágenes
      if (file.type.startsWith('image/')) {

        compressedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });

      } else {
        compressedFile = file;
      }

    } catch (error: any) {
      throw new Error(
        `Error comprimiendo imagen: ${
          error?.message || 'No se pudo comprimir'
        }`
      );
    }

    let base64: string;

    try {
      base64 = await FileUtils.fileToBase64(compressedFile);
    } catch (error: any) {
      throw new Error(
        `Error en FileUtils.fileToBase64: ${
          error?.message || 'No se pudo convertir el archivo'
        }`
      );
    }

    let uploaded: any;

    try {
      uploaded = await fileService.uploadBase64({
        fileName: compressedFile.name,
        mimeType:
          compressedFile.type || 'application/octet-stream',
        base64,
        folder: 'workers/documents',
      });
    } catch (error: any) {
      throw new Error(
        `Error en fileService.uploadBase64: ${
          error?.response?.data?.message ||
          error?.message ||
          'Falló la subida'
        }`
      );
    }

    const documentValue: DocumentFile[] = [
      {
        fileId: uploaded.id,
        name: uploaded.originalName,
        url: uploaded.publicUrl,
        mimeType: uploaded.mimeType,
        size: Number(uploaded.size),
      },
    ];

    try {
      onBlur({ documentValue });
    } catch (error: any) {
      throw new Error(
        `Error en onBlur: ${
          error?.message || 'Falló actualización del formulario'
        }`
      );
    }

    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);

  } catch (uploadError: any) {
    setError(uploadError.message || 'No se pudo subir el documento');
  } finally {
    setIsUploading(false);
  }
};

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[2],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'block',
              marginBottom: spacing[1],
            }}
          >
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>

        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[1],
                color: colors.success?.[500] || '#10b981',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <FileUpload
        onFileSelect={handleFileSelect}
        accept={accept}
        multiple={false}
        hideUploadedFiles
        label="Subir documento"
        helpText="PDF, Word o imagen segun lo solicitado"
      />

      {isUploading && (
        <div
          style={{
            marginTop: spacing[2],
            display: 'flex',
            alignItems: 'center',
            gap: spacing[2],
            color: colors.primary[600],
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            style={{ display: 'flex' }}
          >
            <Loader2 size={14} />
          </motion.div>
          Subiendo documento...
        </div>
      )}

      {currentDocument && (
        <div
          style={{
            marginTop: spacing[3],
            padding: spacing[3],
            borderRadius: 12,
            border: `1px solid ${colors.gray[200]}`,
            backgroundColor: colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing[2],
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
            <FileText size={18} color={colors.primary[600]} />
            <span style={{ fontSize: 14, color: colors.gray[800] }}>{currentDocument.name}</span>
          </div>
          <a
            href={resolvePublicUrl(currentDocument.url)}
            target="_blank"
            rel="noreferrer"
            style={{
              color: colors.primary[600],
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Ver documento
          </a>
        </div>
      )}

      {error && (
        <p style={{ marginTop: spacing[2], color: colors.error[600], fontSize: 13 }}>{error}</p>
      )}
    </div>
  );
}
